import { useState, useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useProcedureTemplates } from '@/lib/hooks/use-procedure-templates';
import { Loader2, ClipboardList, AlertCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { colorWithAlpha } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

/**
 * Génère une abréviation à partir du nom de la procédure
 * @param {string} name
 * @returns {string}
 */
function generateAbbreviation(name) {
    if (!name) return '??';
    const words = name.split(' ').filter((w) => w.length > 2);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
}

/**
 * Sheet pour sélectionner un modèle de procédure
 * Affiche la liste des templates disponibles et permet d'en sélectionner un
 */
export default function SheetSelectProcedure({ open, onClose, onSelect }) {
    const { t } = useTranslation();
    const { templates, isLoading, error } = useProcedureTemplates();
    const [searchQuery, setSearchQuery] = useState('');

    // Filtrer les templates selon la recherche
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) return templates;
        const query = searchQuery.toLowerCase().trim();
        return templates.filter((template) => template.name.toLowerCase().includes(query));
    }, [templates, searchQuery]);

    const handleSelect = (templateUniqId) => {
        onSelect(templateUniqId);
        onClose();
    };

    // Réinitialiser la recherche à la fermeture
    const handleClose = () => {
        setSearchQuery('');
        onClose();
    };

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <SheetContent className="w-[90vw] md:w-[500px]! md:max-w-[500px]! flex flex-col p-0">
                <SheetHeader className="px-6 py-4 border-b border-border">
                    <SheetTitle className="text-lg font-semibold text-foreground">
                        {t('folders:choisirUnModeleDeProcedure')}
                    </SheetTitle>
                </SheetHeader>

                {/* Search Bar */}
                {!isLoading && !error && templates.length > 0 && (
                    <div className="px-4 pt-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder={t('folders:rechercherUneProcedure2')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4">
                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 text-secondary animate-spin" />
                        </div>
                    )}

                    {/* Error State */}
                    {error && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                            <p className="text-[14px] text-muted-foreground">
                                {t('folders:impossibleDeChargerLesModelesDeProcedures')}
                            </p>
                        </div>
                    )}

                    {/* Empty State */}
                    {!isLoading && !error && templates.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-[14px] text-muted-foreground">{t('folders:aucunModeleDeProcedureDisponible')}</p>
                        </div>
                    )}

                    {/* No Search Results */}
                    {!isLoading && !error && templates.length > 0 && filteredTemplates.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Search className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-[14px] text-muted-foreground">{t('folders:aucunResultatPour')}{searchQuery}"</p>
                        </div>
                    )}

                    {/* Templates List - Style identique au Dashboard */}
                    {!isLoading && !error && filteredTemplates.length > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                            {filteredTemplates.map((template) => {
                                const abbreviation = generateAbbreviation(template.name);
                                const color = template.color || null;
                                return (
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="xmd"
                                        key={template.uniq_id}
                                        onClick={() => handleSelect(template.uniq_id)}
                                        className="flex flex-col items-center rounded-lg border border-border hover:shadow-md transition-shadow p-3 cursor-pointer h-auto"
                                        style={{ backgroundColor: colorWithAlpha(color || 'var(--secondary)', '15') }}
                                    >
                                        {/* Icon Circle */}
                                        <div className="w-full flex items-center justify-center rounded-t-lg">
                                            <div
                                                className="w-9 h-9 rounded-full flex items-center justify-center text-primary-foreground text-sm font-semibold"
                                                style={{ backgroundColor: color || 'var(--secondary)' }}
                                            >
                                                {abbreviation}
                                            </div>
                                        </div>
                                        {/* Name */}
                                        <div className="flex items-center justify-center h-[48px]">
                                            <p className="text-xs text-foreground text-center leading-tight text-balance">
                                                {template.name}
                                            </p>
                                        </div>
                                    </Button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
